<template>
  <div v-bind="$attrs">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-if="returnPolicyMessage" id="return-policy-message" :class="{ 'error-color': isFinalSale }" v-html="returnPolicyMessage"></div>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p v-if="showProtectionPlanMessage && isFinalSale" id="return-protection-plan-message" v-html="protectionPlanMessage(false)"></p>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { CUSTOMER_SERVICE_BT, CS_PHONE_NUMERIC, ORDER_DETAILS } from 'config';
import { formatDate } from '@/utils/General';

const props = defineProps({
  product: {
    type: Object,
    default: () => {},
  },
  showProtectionPlanMessage: {
    type: Boolean,
    default: false,
  },
});

const isFinalSale = computed(() => props.product.lastActIndicator || props.product?.isReturnUnallowedFlag);

const protectionPlanMessage = (appendPeriod) => {
  if (props.showProtectionPlanMessage) {
    let baseMessage = '';
    if (props.product.protectionPlanStatus?.toUpperCase() === 'PENDING') {
      baseMessage = ORDER_DETAILS.protectionPlanPendingMessage;
    } else {
      baseMessage = `Your Protection Plan can be canceled anytime at <span class="protection-plan-phone-number">${CS_PHONE_NUMERIC}</span>. If canceled within 30 days from coverage start, you’ll receive full refund; prorated thereafter.`;
    }
    return appendPeriod ? `. ${baseMessage}` : baseMessage;
  }
  return '';
};

const formatReturnBy = (date) => `Return by ${formatDate(date)}${protectionPlanMessage(true)}`;
const formatClosed = (date) => `The deadline to return this item closed on ${formatDate(date)}`;
const formatReturnPolicyDays = (returnPolicyDays) => `The return policy for this item is ${returnPolicyDays} days${protectionPlanMessage(true)}`;

const deliveredMessage = (returnWindowDate) =>
  Date.parse(returnWindowDate) >= Date.now() ? formatReturnBy(returnWindowDate) : formatClosed(returnWindowDate);

const inTransitMessage = (returnPolicyDays, returnWindowDate) => {
  if (returnPolicyDays) return formatReturnPolicyDays(returnPolicyDays);
  if (returnWindowDate) return formatReturnBy(returnWindowDate);
  return '';
};

const getReturnWindowMessage = (product) => {
  if (!product.returnWindowDate) return null;

  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  today.setHours(0, 0, 0, 0);
  const returnDate = new Date(new Date(product.returnWindowDate.replace('.', '')).toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return returnDate >= today ? formatReturnBy(returnDate) : `This item(s) return window closed on ${formatDate(returnDate)}`;
};

const getShipmentMessage = (product) => {
  switch (product.shipmentStatusCode) {
    case 205:
    case 206:
    case 207: // big ticket
      return `For furniture or mattress returns please call ${CUSTOMER_SERVICE_BT}`;
    case 101: // on the way
      if (product.returnPolicyDays) return formatReturnPolicyDays(product.returnPolicyDays);

      if (product.returnWindowDate) {
        return formatReturnBy(product.returnWindowDate);
      }

      return '';
    case 102:
    case 106:
    case 402:
    case 407:
      if (['EGC', 'VGC'].includes(product.shipmentType) || !product.returnWindowDate) return '';
      return getReturnWindowMessage(product);
    case 300:
    case 301:
    case 302:
    case 303:
    case 304:
      return '';
    default:
      return null;
  }
};

const getPolicyMessage = (product = {}) => {
  if (product.lastActIndicator) {
    return 'Last Act items are final sale, no returns allowed';
  }
  if (product.isReturnUnallowedFlag) {
    return 'This item is final sale and cannot be returned';
  }

  const shipmentMessage = getShipmentMessage(product);
  if (shipmentMessage !== null) {
    return shipmentMessage;
  }

  // In transit return
  if (product.inTransit && product.returnEligibleFlag) return inTransitMessage(product.returnPolicyDays, product.returnWindowDate);

  // Delivered & returnWindow
  if (!product.inTransit && product.returnWindowDate && product.returnEligibleFlag) return deliveredMessage(product.returnWindowDate);

  //protection plan pending message
  if (props.showProtectionPlanMessage && product.protectionPlanStatus?.toUpperCase() === 'PENDING') {
    return ORDER_DETAILS.protectionPlanPendingMessage;
  }

  return '';
};

const returnPolicyMessage = computed(() => getPolicyMessage(props.product));
</script>
<style lang="scss" scoped>
::v-deep(.protection-plan-phone-number) {
  white-space: nowrap;
}
</style>
